import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBirthdayCake, faVenusMars, faRulerVertical, faWeightHanging,
    faBullseye as faGoalTarget, faBan, faAppleAlt, faFire,
    faUserCircle, faCalculator, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
// Importar helpers y componente CalorieMacroCalculator
import CalorieMacroCalculator, { calculateBMR, calculateTDEE, adjustCaloriesForGoal, activityFactors } from './CalorieMacroCalculator'; // Ajusta la ruta
// import './OnboardingForm.css';

const DEFAULT_MACROS = { protein: 30, carb: 40, fat: 30 };
const DEFAULT_TARGET_CALORIES = 2000; // Un valor inicial antes de cualquier cálculo

const initialFormData = {
    // Perfil
    age: '', sex: '', height: '', weight: '',
    activityLevel: 'sedentary', primary_goal: 'maintain_weight',
    // Preferencias
    dietaryPreferences: '', allergiesOrRestrictions: '',
    // Objetivos
    targetCalories: DEFAULT_TARGET_CALORIES, // Iniciar con un default razonable
    proteinPercent: DEFAULT_MACROS.protein,
    carbPercent: DEFAULT_MACROS.carb,
    fatPercent: DEFAULT_MACROS.fat,
};

const OnboardingForm = ({ show, onSubmit, userEmail, currentProfileData }) => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState(() => ({ ...initialFormData })); // Usar función para estado inicial
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const totalSteps = 4;

    // Función para calcular calorías sugeridas basada en el estado actual de formData
    const calculateSuggestedCaloriesFromFormData = useCallback((data) => {
        const { age, sex, height, weight, activityLevel, primary_goal } = data;
        if (age && sex && height && weight) {
            const bmr = calculateBMR(parseFloat(weight), parseInt(height, 10), parseInt(age, 10), sex);
            const tdee = calculateTDEE(bmr, activityLevel);
            return tdee ? adjustCaloriesForGoal(tdee, primary_goal, sex) : DEFAULT_TARGET_CALORIES;
        }
        return DEFAULT_TARGET_CALORIES; // Default si el perfil no está completo
    }, []);

    // Efecto para inicializar y sincronizar con currentProfileData
    useEffect(() => {
        if (show) {
            let newFd = { ...initialFormData };

            if (currentProfileData) {
                newFd.age = String(currentProfileData.age || '');
                newFd.sex = currentProfileData.sex || '';
                newFd.height = String(currentProfileData.height || '');
                newFd.weight = String(currentProfileData.weight || '');
                newFd.activityLevel = currentProfileData.activityLevel || currentProfileData.activity_level || initialFormData.activityLevel;
                newFd.primary_goal = currentProfileData.primary_goal || initialFormData.primary_goal;
                newFd.dietaryPreferences = currentProfileData.dietaryPreferences || '';
                newFd.allergiesOrRestrictions = Array.isArray(currentProfileData.restrictions)
                    ? currentProfileData.restrictions.join(', ')
                    : (currentProfileData.allergiesOrRestrictions || '');

                const tc = Number(currentProfileData.target_calories);
                if (tc >= 1000 && tc <= 6000) {
                    newFd.targetCalories = tc;
                } else {
                    newFd.targetCalories = calculateSuggestedCaloriesFromFormData(newFd);
                }

                const p = Number(currentProfileData.protein_percent_target);
                const c = Number(currentProfileData.carb_percent_target);
                const f = Number(currentProfileData.fat_percent_target);
                if (p > 0 && c > 0 && f > 0 && (Math.round(p + c + f) === 100)) {
                    newFd.proteinPercent = p;
                    newFd.carbPercent = c;
                    newFd.fatPercent = f;
                } else {
                    // Mantener defaults de initialFormData si los del perfil son inválidos
                    newFd.proteinPercent = initialFormData.proteinPercent;
                    newFd.carbPercent = initialFormData.carbPercent;
                    newFd.fatPercent = initialFormData.fatPercent;
                }
            } else {
                // Si no hay currentProfileData, targetCalories y macros ya están en sus defaults de initialFormData
                // No es necesario calcular sugerencias aquí ya que el perfil está vacío.
            }
            setFormData(newFd);
            setStep(1);
            setError('');
        }
    }, [show, currentProfileData, calculateSuggestedCaloriesFromFormData]);

    // Efecto para actualizar targetCalories si el perfil cambia y las calorías son las iniciales "no tocadas"
    // Esto ayuda a que las calorías se actualicen dinámicamente mientras el usuario llena el perfil.
    const isInitialCalories = formData.targetCalories === DEFAULT_TARGET_CALORIES || formData.targetCalories === 0;
    useEffect(() => {
        if (show && !currentProfileData && isInitialCalories) { // Solo si no hay perfil cargado y las calorías son las default/no tocadas
            const { age, sex, height, weight } = formData;
            if (age && sex && height && weight) { // Y el perfil tiene datos suficientes
                const suggested = calculateSuggestedCaloriesFromFormData(formData);
                if (formData.targetCalories !== suggested) {
                    setFormData(prev => ({ ...prev, targetCalories: suggested }));
                }
            }
        }
    }, [
        show, currentProfileData, isInitialCalories, // Controla cuándo se ejecuta
        formData.age, formData.sex, formData.height, formData.weight, // Disparadores de cálculo
        formData.activityLevel, formData.primary_goal, // Disparadores de cálculo
        calculateSuggestedCaloriesFromFormData,
        // No incluir formData.targetCalories para evitar bucles, `isInitialCalories` controla eso
    ]);


    const handleProfileInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const nextStep = () => {
        setError('');
        let isValid = true;
        if (step === 1) {
            if (!formData.age || !formData.sex || !formData.height || !formData.weight) {
                setError("Please fill in all personal details."); isValid = false;
            } else if (isNaN(parseInt(formData.age, 10)) || parseInt(formData.age, 10) <= 0 ||
                isNaN(parseInt(formData.height, 10)) || parseInt(formData.height, 10) <= 0 ||
                isNaN(parseFloat(formData.weight)) || parseFloat(formData.weight) <= 0) {
                setError("Age, height, and weight must be valid positive numbers."); isValid = false;
            }
        }
        if (step === 3 && isValid) {
            if (!formData.primary_goal || formData.primary_goal === "") { setError("Please select your primary health goal."); isValid = false; }
            if (!formData.activityLevel) { setError("Please select your activity level."); isValid = false; }
        }

        // Al pasar del paso 3 al 4, si las calorías aún son el valor default y el perfil está completo, calcularlas
        if (step === 3 && isValid) {
            const { age, sex, height, weight, targetCalories } = formData;
            if (age && sex && height && weight && (targetCalories === DEFAULT_TARGET_CALORIES || targetCalories === 0 || targetCalories === undefined)) {
                const suggested = calculateSuggestedCaloriesFromFormData(formData);
                if (formData.targetCalories !== suggested) {
                    setFormData(prev => ({ ...prev, targetCalories: suggested }));
                }
            }
        }

        if (isValid) {
            setStep(prev => Math.min(prev + 1, totalSteps));
            if (typeof window !== 'undefined') window.scrollTo(0, 0);
        }
    };

    const prevStep = () => {
        setError('');
        setStep(prev => Math.max(prev - 1, 1));
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    };

    const handleCalculatorTargetsChange = useCallback((newTargets) => {
        setFormData(prev => ({
            ...prev,
            targetCalories: newTargets.calories, // Viene del CalorieMacroCalculator
            proteinPercent: newTargets.protein,
            carbPercent: newTargets.carb,
            fatPercent: newTargets.fat,
        }));
    }, []);

    const handleFinalOnboardingSubmit = async () => {
        setError('');
        // Validaciones de perfil
        if (!formData.age || !formData.sex || !formData.height || !formData.weight ||
            !formData.primary_goal || !formData.activityLevel) {
            setError("Please complete all personal, health, and activity details.");
            if (!formData.age || !formData.sex || !formData.height || !formData.weight) setStep(1);
            else if (!formData.primary_goal || !formData.activityLevel) setStep(3);
            return;
        }
        // Validaciones de targets (del formData, que es la fuente de verdad)
        const { targetCalories, proteinPercent, carbPercent, fatPercent } = formData;
        if (targetCalories < 1000 || targetCalories > 6000) {
            setError("Target Calories must be between 1000 and 6000. Please adjust in Step 4.");
            setStep(4); return;
        }
        if (proteinPercent <= 0 || carbPercent <= 0 || fatPercent <= 0 ||
            proteinPercent > 100 || carbPercent > 100 || fatPercent > 100) {
            setError("Macronutrient percentages must be valid (1-100). Please review Step 4.");
            setStep(4); return;
        }
        if (Math.round(proteinPercent + carbPercent + fatPercent) !== 100) {
            setError(`Macronutrient percentages must sum to 100%. Current sum: ${Math.round(proteinPercent + carbPercent + fatPercent)}%. Please adjust in Step 4.`);
            setStep(4); return;
        }

        setIsLoading(true);
        try {
            // Calcular BMR y TDEE AHORA, usando el formData final
            const bmrValue = calculateBMR(
                parseFloat(formData.weight),
                parseInt(formData.height, 10),
                parseInt(formData.age, 10),
                formData.sex
            );

            const tdeeValue = calculateTDEE(bmrValue, formData.activityLevel);

            const submissionData = {
                age: Number(formData.age),
                sex: formData.sex,
                height: Number(formData.height),
                weight: Number(formData.weight),
                activity_level: formData.activityLevel, // Mapear a activity_level si backend lo espera así
                primary_goal: formData.primary_goal,
                dietaryPreferences: formData.dietaryPreferences,
                allergiesOrRestrictions: formData.allergiesOrRestrictions,
                target_calories: targetCalories,
                protein_percent_target: proteinPercent,
                carb_percent_target: carbPercent,
                fat_percent_target: fatPercent,
                bmr: bmrValue ? Math.round(bmrValue) : null,      // ¡AÑADIDO!
                tdee: tdeeValue ? Math.round(tdeeValue) : null,    // ¡AÑADIDO!
            };
            await onSubmit(submissionData);
        } catch (submitError) {
            setError(submitError.message || "An error occurred while saving your information. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const renderProgressBar = () => { /* ... (JSX sin cambios) ... */
        return (
            <div className="custom-progress-bar-container mb-4">
                <div className="progress-segments d-flex justify-content-between">
                    {[...Array(totalSteps)].map((_, i) => (
                        <div key={i} className={`progress-segment ${i < step ? 'active' : ''} ${i === step - 1 ? 'current' : ''}`} />
                    ))}
                </div>
                <p className="text-center text-sm text-muted mt-2 small">
                    Step {step} of {totalSteps}: {" "}
                    {step === 1 ? "Personal Details" :
                        step === 2 ? "Dietary Information" :
                            step === 3 ? "Health & Fitness Goals" : "Review Estimated Needs"}
                </p>
            </div>
        );
    };

    return (
        <Modal show={show} backdrop="static" keyboard={false} centered size="lg" scrollable>
            <Modal.Header>
                <Modal.Title>
                    <FontAwesomeIcon icon={faUserCircle} className="me-2" />
                    Welcome{userEmail ? `, ${userEmail.split('@')[0]}!` : '!'} Let's Personalize Your Experience
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {renderProgressBar()}
                {error && <Alert variant="danger" onClose={() => setError('')} dismissible className="mb-3">{error}</Alert>}

                {step === 1 && (
                    <div className="animate__animated animate__fadeIn">
                        <h5 className="text-primary mb-3"><FontAwesomeIcon icon={faUserCircle} className="me-2" />Personal Details</h5>
                        <Row className="g-3">
                            <Col md={6}><Form.Group controlId="onboardingAge"><Form.Label><FontAwesomeIcon icon={faBirthdayCake} className="me-2 text-muted" />Age</Form.Label><Form.Control type="number" name="age" placeholder="Your age" value={formData.age} onChange={handleProfileInputChange} required min="1" /></Form.Group></Col>
                            <Col md={6}><Form.Group controlId="onboardingSex"><Form.Label><FontAwesomeIcon icon={faVenusMars} className="me-2 text-muted" />Sex</Form.Label><Form.Select name="sex" value={formData.sex} onChange={handleProfileInputChange} required><option value="">Select...</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></Form.Select></Form.Group></Col>
                        </Row>
                        <Row className="g-3 mt-2">
                            <Col md={6}><Form.Group controlId="onboardingHeight"><Form.Label><FontAwesomeIcon icon={faRulerVertical} className="me-2 text-muted" />Height (cm)</Form.Label><Form.Control type="number" name="height" placeholder="e.g., 175" value={formData.height} onChange={handleProfileInputChange} required min="50" /></Form.Group></Col>
                            <Col md={6}><Form.Group controlId="onboardingWeight"><Form.Label><FontAwesomeIcon icon={faWeightHanging} className="me-2 text-muted" />Weight (kg)</Form.Label><Form.Control type="number" name="weight" placeholder="e.g., 70" step="0.1" value={formData.weight} onChange={handleProfileInputChange} required min="10" /></Form.Group></Col>
                        </Row>
                    </div>
                )}
                {step === 2 && (
                    <div className="animate__animated animate__fadeIn">
                        <h5 className="text-primary mb-3"><FontAwesomeIcon icon={faAppleAlt} className="me-2" />Dietary Information</h5>
                        <Form.Group className="mb-3" controlId="onboardingDietaryPreferences"><Form.Label>Any Specific Dietary Preferences?</Form.Label><Form.Select name="dietaryPreferences" value={formData.dietaryPreferences} onChange={handleProfileInputChange}><option value="">None / Standard</option><option value="vegetarian">Vegetarian</option><option value="vegan">Vegan</option><option value="pescatarian">Pescatarian</option><option value="gluten-free">Gluten-Free</option><option value="dairy-free">Dairy-Free</option><option value="keto">Ketogenic</option><option value="paleo">Paleo</option><option value="low-fodmap">Low-FODMAP</option></Form.Select></Form.Group>
                        <Form.Group controlId="onboardingAllergiesOrRestrictions"><Form.Label><FontAwesomeIcon icon={faBan} className="me-2 text-muted" />Food Allergies or Other Restrictions</Form.Label><Form.Control as="textarea" rows={3} name="allergiesOrRestrictions" placeholder="List any foods (comma-separated)" value={formData.allergiesOrRestrictions} onChange={handleProfileInputChange} /><Form.Text className="text-muted">These will be added to your profile restrictions.</Form.Text></Form.Group>
                    </div>
                )}
                {step === 3 && (
                    <div className="animate__animated animate__fadeIn">
                        <h5 className="text-primary mb-3"><FontAwesomeIcon icon={faGoalTarget} className="me-2" />Health & Fitness Goals</h5>
                        <Form.Group className="mb-3" controlId="onboardingPrimaryGoal"><Form.Label>What's your primary health goal?</Form.Label><Form.Select name="primary_goal" value={formData.primary_goal} onChange={handleProfileInputChange} required><option value="maintain_weight">Maintain Weight</option><option value="lose_weight">Lose Weight</option><option value="gain_muscle">Gain Muscle</option><option value="improve_endurance">Improve Endurance</option><option value="eat_healthier">Eat Healthier Generally</option><option value="reduce_stress">Reduce Stress</option></Form.Select></Form.Group>
                        <Form.Group controlId="onboardingActivityLevel"><Form.Label><FontAwesomeIcon icon={faFire} className="me-2 text-muted" />Activity Level</Form.Label><Form.Select name="activityLevel" value={formData.activityLevel} onChange={handleProfileInputChange} required><option value="sedentary">Sedentary (little/no exercise)</option><option value="light">Light (exercise 1-3x/wk)</option><option value="moderate">Moderate (exercise 3-5x/wk)</option><option value="active">Active (exercise 6-7x/wk)</option><option value="very_active">Very Active (intense daily)</option></Form.Select></Form.Group>
                    </div>
                )}
                {step === 4 && (
                    <div className="animate__animated animate__fadeIn">
                        <h5 className="text-primary mb-1">
                            <FontAwesomeIcon icon={faCalculator} className="me-2" /> Review & Adjust Your Estimated Needs
                        </h5>
                        <p className="text-muted mb-3 small">
                            Here are your estimated daily targets based on your inputs. You can adjust them now, or later.
                        </p>
                        <CalorieMacroCalculator
                            profileData={formData} // Pasar todo el formData actual que incluye el perfil
                            currentCalories={formData.targetCalories}
                            currentProteinPercent={formData.proteinPercent}
                            currentCarbPercent={formData.carbPercent}
                            currentFatPercent={formData.fatPercent}
                            onTargetsChange={handleCalculatorTargetsChange}
                            areProfileInputsEditable={false} // El perfil se edita en pasos 1 y 3
                            areTargetsEditable={true} // Sí, queremos que el usuario ajuste aquí
                            showApplyButton={false} // No necesita botón "Apply" interno
                            cardHeaderTitle="Adjust Your Daily Targets"
                            showSuggestionsBasedOnProfile={true}
                        />
                        <Alert variant="light" className="mt-4 small text-muted">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                            Fine-tuning these targets now will help generate a more personalized initial meal plan. The total of Protein, Carbs, and Fat percentages must be 100%.
                        </Alert>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                {step > 1 ? (<Button variant="outline-secondary" onClick={prevStep} disabled={isLoading} type="button">Previous</Button>) : (<div></div>)}
                {step < totalSteps ? (
                    <Button variant="primary" onClick={nextStep} disabled={isLoading} type="button">Next</Button>
                ) : (
                    <Button variant="success" onClick={handleFinalOnboardingSubmit} disabled={isLoading} type="button">
                        {isLoading ? <Spinner as="span" animation="border" size="sm" className="me-1" /> : ''}
                        Finish Setup & Go to App
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

OnboardingForm.propTypes = {
    show: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    userEmail: PropTypes.string,
    currentProfileData: PropTypes.shape({
        age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sex: PropTypes.string,
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        activityLevel: PropTypes.string,
        activity_level: PropTypes.string, // Considerar mapear a activityLevel
        primary_goal: PropTypes.string,
        dietaryPreferences: PropTypes.string,
        allergiesOrRestrictions: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        restrictions: PropTypes.arrayOf(PropTypes.string), // Considerar mapear
        target_calories: PropTypes.number,
        protein_percent_target: PropTypes.number,
        carb_percent_target: PropTypes.number,
        fat_percent_target: PropTypes.number,
    })
};

export default OnboardingForm;